import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TITLES, COUNTRIES, isBusinessEmail } from "./utils";
import { ShieldCheck, X } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";
import { displayCompany } from "../../branding/tenants";

export default function ProfileGate({ open, session, onSave, submitting, dismissible = false, onDismiss }) {
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [dial, setDial] = useState(
    COUNTRIES.find((c) => c.code === session?.country_code)?.dial || "+31"
  );
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState({});

  // Reset dial code when the persona / session changes
  useEffect(() => {
    const fresh = COUNTRIES.find((c) => c.code === session?.country_code)?.dial;
    if (fresh) setDial(fresh);
    setEmail("");
    setPhone("");
    setTitle("");
    setErrors({});
  }, [session?.network_id, session?.country_code]);

  const handleSubmit = () => {
    const next = {};
    if (!email.trim()) next.email = "Business email is required";
    else if (!isBusinessEmail(email)) next.email = "Personal email addresses are not allowed";
    if (!phone.trim()) next.phone = "Phone number is required";
    if (!title) next.title = "Select your title";
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      business_email: email.trim(),
      country_dial_code: dial,
      phone_number: phone.trim(),
      title,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && dismissible && onDismiss) onDismiss(); }}>
      <DialogContent
        className="max-w-lg"
        data-testid="profile-gate-dialog"
        onPointerDownOutside={(e) => { if (!dismissible) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!dismissible) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!dismissible) e.preventDefault(); }}
      >
        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            data-testid="profile-gate-close"
            className="absolute right-4 top-4 h-8 w-8 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 flex items-center justify-center transition-colors"
          >
            <X size={16} />
          </button>
        )}
        <DialogHeader>
          <div className="h-11 w-11 rounded-md bg-[#0072CE]/10 border border-[#0072CE]/15 text-[#0072CE] flex items-center justify-center mb-3">
            <ShieldCheck size={20} />
          </div>
          <DialogTitle className="font-display tracking-tight text-2xl">
            {t("profile.welcome")}
          </DialogTitle>
          <DialogDescription>
            {t("profile.first_time")}<br />
            <span className="font-semibold text-slate-900">{session?.network_id}</span> · {displayCompany(session?.company_name)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-sm font-semibold text-slate-800">
              {t("profile.email")} <span className="text-rose-600">*</span>
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`first.last@${(displayCompany(session?.company_name) || "company").toLowerCase().replace(/\s+/g, "")}.com`}
              className="mt-1.5"
              data-testid="profile-email"
            />
            <p className={`mt-1 text-xs ${errors.email ? "text-rose-600" : "text-slate-500"}`}>
              {errors.email || "Personal email domains (gmail, yahoo, outlook…) are blocked."}
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">
              {t("profile.phone")} <span className="text-rose-600">*</span>
            </Label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              <Select value={dial} onValueChange={setDial}>
                <SelectTrigger data-testid="profile-dial"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.dial}>
                      {c.code} {c.dial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="col-span-2"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                data-testid="profile-phone"
              />
            </div>
            {errors.phone && <p className="mt-1 text-xs text-rose-600">{errors.phone}</p>}
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-800">
              {t("profile.title")} <span className="text-rose-600">*</span>
            </Label>
            <Select value={title} onValueChange={setTitle}>
              <SelectTrigger className="mt-1.5" data-testid="profile-title">
                <SelectValue placeholder={t("profile.title")} />
              </SelectTrigger>
              <SelectContent>
                {TITLES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`profile-title-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.title && <p className="mt-1 text-xs text-rose-600">{errors.title}</p>}
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="profile-save"
          className="mt-2 w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold"
        >
          {submitting ? "…" : t("profile.continue")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
